import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { supabase } from '@lib/supabase';
import type { FeedbackRecord } from '@lib/api';
import { deleteFeedback, listFeedback } from '@lib/feedback';
import { useAuth } from '@lib/useAuth';

const ADMIN_API_BASE =
  import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api';
const ADMIN_FEEDBACK_EMAIL_PATH = '/admin-feedback-email';
const ADMIN_FEEDBACK_EMAIL_ENDPOINT = `${ADMIN_API_BASE}${ADMIN_FEEDBACK_EMAIL_PATH}`;

type RecipientInfo = {
  email: string;
  fullName: string | null;
  displayName: string | null;
};

const isErrorResponse = (value: unknown): value is { error?: string } =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'error' in value &&
      typeof (value as { error?: unknown }).error === 'string'
  );

type EmailTemplate = {
  id: string;
  label: string;
  subject: string;
  buildMessageHtml: (entry: FeedbackRecord) => string;
};

const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'follow-up',
    label: 'Follow-up question',
    subject: 'Follow-up on your feedback — Xhalin',
    buildMessageHtml: () => {
      return `<p>Thank you for sharing your idea:</p>
<p>I just have a question about it and would love a few more details. Could you tell me a bit more about how you picture this working?</p>
`;
    },
  },
  {
    id: 'status',
    label: 'Status update',
    subject: 'Your feedback is on our radar — Xhalin',
    buildMessageHtml: () => {
      return `<p>We wanted to let you know we read your note.</p>
<p>We will keep you posted as we evaluate it with the team.</p>
`;
    },
  },
];

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function shortId(id: string): string {
  return `${id.slice(0, 6)}…`;
}

function stripHtml(value: string): string {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function AdminFeedback() {
  const [feedback, setFeedback] = useState<FeedbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmingFeedback, setConfirmingFeedback] =
    useState<FeedbackRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [userNames, setUserNames] = useState<Record<string, string | null>>({});
  const { session } = useAuth();
  const [emailModalFeedback, setEmailModalFeedback] =
    useState<FeedbackRecord | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessageHtml, setEmailMessageHtml] = useState('');
  const [recipientInfo, setRecipientInfo] = useState<RecipientInfo | null>(
    null
  );
  const [isFetchingRecipient, setIsFetchingRecipient] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccessMessage, setEmailSuccessMessage] = useState<string | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;

    const loadFeedback = async () => {
      setLoading(true);
      setError(null);

      try {
        const rows = await listFeedback();
        if (cancelled) return;
        setFeedback(rows);
        await loadDisplayNames(rows);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : 'Unable to load feedback right now.';
        setError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const loadDisplayNames = async (rows: FeedbackRecord[]) => {
      const ids = Array.from(
        new Set(rows.map((row) => row.user_id).filter(Boolean) as string[])
      );
      if (ids.length === 0) {
        setUserNames({});
        return;
      }

      const { data: settings, error } = await supabase
        .from('user_settings')
        .select('user_id, display_name')
        .in('user_id', ids);

      if (cancelled) return;

      if (error || !settings) {
        setUserNames({});
        return;
      }

      const map: Record<string, string | null> = {};
      const rowsWithNames =
        (settings as { user_id: string; display_name: string | null }[]) ?? [];
      rowsWithNames.forEach((row) => {
        map[row.user_id] = row.display_name ?? null;
      });
      setUserNames(map);
    };

    loadFeedback();

    return () => {
      cancelled = true;
    };
  }, []);

  const openDeleteDialog = (entry: FeedbackRecord) => {
    setConfirmingFeedback(entry);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!confirmingFeedback) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteFeedback(confirmingFeedback.id);
      setFeedback((prev) =>
        prev.filter((item) => item.id !== confirmingFeedback.id)
      );
      setConfirmingFeedback(null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to delete feedback right now.';
      setDeleteError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const getUserLabel = (entry: FeedbackRecord) => {
    if (!entry.user_id) {
      return 'Guest (anonymous)';
    }
    const displayName = userNames[entry.user_id];
    if (displayName) {
      return displayName;
    }
    return `User • ${shortId(entry.user_id)}`;
  };

  const openEmailDialog = (entry: FeedbackRecord) => {
    if (!entry.user_id) {
      return;
    }
    setEmailModalFeedback(entry);
    setSelectedTemplateId('');
    setEmailSubject('');
    setEmailMessageHtml('');
    setRecipientInfo(null);
    setRecipientError(null);
    setEmailError(null);
    setEmailSuccessMessage(null);
    setIsFetchingRecipient(false);
  };

  const closeEmailDialog = () => {
    setEmailModalFeedback(null);
    setSelectedTemplateId('');
    setEmailSubject('');
    setEmailMessageHtml('');
    setRecipientInfo(null);
    setRecipientError(null);
    setEmailError(null);
    setEmailSuccessMessage(null);
    setIsFetchingRecipient(false);
    setIsSendingEmail(false);
  };

  const messageTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const formatSelection = (tag: 'strong' | 'em') => {
    const textarea = messageTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const before = emailMessageHtml.slice(0, start);
    const selected = emailMessageHtml.slice(start, end);
    const after = emailMessageHtml.slice(end);
    const formatted = `${before}<${tag}>${selected}</${tag}>${after}`;
    setEmailMessageHtml(formatted);

    const tagLength = tag.length + 2; // <tag>
    const cursorStart = start + tagLength;
    const cursorEnd = cursorStart + selected.length;
    requestAnimationFrame(() => {
      if (!messageTextareaRef.current) return;
      messageTextareaRef.current.selectionStart = cursorStart;
      messageTextareaRef.current.selectionEnd = cursorEnd;
      messageTextareaRef.current.focus();
    });
  };

  const insertLink = () => {
    const textarea = messageTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const selected = emailMessageHtml.slice(start, end) || 'link';
    const url = window.prompt('Enter the link URL', 'https://');
    if (!url) return;
    const before = emailMessageHtml.slice(0, start);
    const after = emailMessageHtml.slice(end);
    const formatted = `${before}<a href="${url}">${selected}</a>${after}`;
    setEmailMessageHtml(formatted);

    const cursorStart = start + `<a href="${url}">`.length;
    const cursorEnd = cursorStart + selected.length;
    requestAnimationFrame(() => {
      if (!messageTextareaRef.current) return;
      messageTextareaRef.current.selectionStart = cursorStart;
      messageTextareaRef.current.selectionEnd = cursorEnd;
      messageTextareaRef.current.focus();
    });
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = EMAIL_TEMPLATES.find((item) => item.id === templateId);
    if (template && emailModalFeedback) {
      setEmailSubject(template.subject);
      setEmailMessageHtml(template.buildMessageHtml(emailModalFeedback));
    }
  };

  useEffect(() => {
    const userId = emailModalFeedback?.user_id;
    if (!userId) {
      setRecipientInfo(null);
      setRecipientError(null);
      setIsFetchingRecipient(false);
      return;
    }

    if (!session?.access_token) {
      return;
    }

    let cancelled = false;

    const fetchRecipient = async () => {
      setIsFetchingRecipient(true);
      setRecipientError(null);
      try {
        const response = await fetch(
          `${ADMIN_FEEDBACK_EMAIL_ENDPOINT}?userId=${encodeURIComponent(
            userId
          )}`,
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok) {
          throw new Error(
            (isErrorResponse(data) ? data.error : undefined) ??
              'Unable to load email address.'
          );
        }
        setRecipientInfo(data as RecipientInfo);
      } catch (err) {
        if (cancelled) return;
        setRecipientInfo(null);
        setRecipientError(
          err instanceof Error
            ? err.message
            : 'Unable to load the recipient email right now.'
        );
      } finally {
        if (!cancelled) {
          setIsFetchingRecipient(false);
        }
      }
    };

    fetchRecipient();

    return () => {
      cancelled = true;
    };
  }, [emailModalFeedback?.user_id, session?.access_token]);

  const messagePlainText = useMemo(
    () => stripHtml(emailMessageHtml),
    [emailMessageHtml]
  );

  const handleSendEmail = async () => {
    if (!emailModalFeedback?.user_id || !recipientInfo?.email) {
      return;
    }
    if (!session?.access_token) {
      setEmailError('Unable to authenticate. Please refresh.');
      return;
    }

    const trimmedSubject = emailSubject.trim();
    const trimmedMessage = messagePlainText.trim();
    if (!trimmedSubject || !trimmedMessage) {
      setEmailError('Subject and message are required to send an email.');
      return;
    }

    setEmailError(null);
    setEmailSuccessMessage(null);
    setIsSendingEmail(true);

    try {
      const trimmedHtmlMessage = emailMessageHtml.trim();
      const response = await fetch(ADMIN_FEEDBACK_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId: emailModalFeedback.user_id,
          feedbackId: emailModalFeedback.id,
          subject: trimmedSubject,
          message: trimmedMessage,
          messageHtml: trimmedHtmlMessage || undefined,
          templateId: selectedTemplateId || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error ?? 'Unable to send the email right now.');
      }

      setEmailSuccessMessage('Email sent successfully.');
    } catch (err) {
      setEmailError(
        err instanceof Error
          ? err.message
          : 'Unable to send the email right now.'
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  const canSendEmail =
    Boolean(recipientInfo?.email) &&
    emailSubject.trim().length > 0 &&
    messagePlainText.trim().length > 0 &&
    !isSendingEmail &&
    !isFetchingRecipient;

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-5xl px-4 py-12">
        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="flex flex-col gap-3 text-center sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
                Feedback{' '}
              </h1>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-32 animate-pulse rounded-2xl border border-gray-100 bg-white/60"
                  />
                ))}
              </div>
            ) : feedback.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No feedback submissions yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                <table className="min-w-full text-left text-sm text-gray-800">
                  <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Page</th>
                      <th className="px-4 py-3">Message</th>
                      <th className="px-4 py-3">Submitted by</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedback.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-t border-gray-100 hover:bg-indigo-50"
                      >
                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                          {formatDateTime(entry.created_at)}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-teal-700">
                            {entry.category}
                          </span>
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                          {entry.page ?? 'Global'}
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                          <p className="max-w-[32rem] whitespace-pre-line">
                            {entry.message}
                          </p>
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                          {getUserLabel(entry)}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {entry.user_id && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="!text-slate-700 hover:!bg-slate-100 focus:!ring-slate-300"
                                onClick={() => openEmailDialog(entry)}
                              >
                                Email
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                              onClick={() => openDeleteDialog(entry)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmingFeedback && (
        <Modal
          title="Confirm delete"
          onClose={() => setConfirmingFeedback(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Delete the feedback submitted by{' '}
              <span className="font-semibold">
                {getUserLabel(confirmingFeedback)}
              </span>
              ? This cannot be undone.
            </p>
            <p className="text-sm text-gray-500">
              Message preview: "{confirmingFeedback.message}"
            </p>
            {deleteError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setConfirmingFeedback(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {emailModalFeedback && (
        <Modal
          title="Email User"
          onClose={closeEmailDialog}
          className="max-w-none"
          style={{ width: '50vw', minWidth: '320px' }}
        >
          <div className="flex max-h-[72vh] w-full min-h-[350px] flex-col gap-4 overflow-hidden pr-2 text-sm text-gray-700">
            <div className="flex-1 space-y-4 overflow-y-auto pr-1">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Template
                </label>
                <select
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  value={selectedTemplateId}
                  onChange={(event) => handleTemplateChange(event.target.value)}
                >
                  <option value="">-- Select template --</option>
                  {EMAIL_TEMPLATES.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Templates prefill the subject and message. You can still edit
                  everything before sending.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  To
                </label>
                <input
                  type="text"
                  readOnly
                  value={recipientInfo?.email ?? ''}
                  placeholder={
                    isFetchingRecipient
                      ? 'Looking up recipient...'
                      : 'Recipient email will appear here'
                  }
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                {recipientInfo?.displayName && (
                  <p className="text-xs text-gray-500">
                    {recipientInfo.displayName}
                  </p>
                )}
                {recipientError && (
                  <p className="text-xs font-semibold text-rose-600">
                    {recipientError}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Message
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    aria-label="Bold"
                    className="h-8 w-8 rounded-full border border-gray-200 bg-white text-sm font-bold text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                    onClick={() => formatSelection('strong')}
                  >
                    B
                  </button>
                  <button
                    type="button"
                    aria-label="Italic"
                    className="h-8 w-8 rounded-full border border-gray-200 bg-white text-sm font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                    onClick={() => formatSelection('em')}
                  >
                    I
                  </button>
                  <button
                    type="button"
                    aria-label="Link"
                    className="h-8 w-8 rounded-full border border-gray-200 bg-white text-sm font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                    onClick={insertLink}
                  >
                    dY"-
                  </button>
                </div>
                <textarea
                  ref={messageTextareaRef}
                  value={emailMessageHtml}
                  onChange={(event) => setEmailMessageHtml(event.target.value)}
                  rows={10}
                  cols={90}
                  className="max-h-[220px] min-h-[220px] w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white px-3 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>

              {emailError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                  {emailError}
                </div>
              )}
              {emailSuccessMessage && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                  {emailSuccessMessage}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={closeEmailDialog}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="item-selected"
                size="sm"
                onClick={handleSendEmail}
                disabled={!canSendEmail}
              >
                {isSendingEmail ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdminFeedback;
